import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { api } from '../services/api';

interface StripeConfig {
  publishableKey: string;
  webhookEndpoint: string;
  isConfigured: boolean;
}

interface StripeProduct {
  id: string;
  name: string;
  description?: string;
  prices: StripePrice[];
  created: number;
  active: boolean;
}

interface StripePrice {
  id: string;
  product: string;
  unit_amount: number;
  currency: string;
  type: 'one_time' | 'recurring';
  active: boolean;
  nickname?: string;
}

export const StripeConfigPage: React.FC = () => {
  const [config, setConfig] = useState<StripeConfig>({
    publishableKey: '',
    webhookEndpoint: '',
    isConfigured: false,
  });
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testMode] = useState(true);

  // Form state for creating new products
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'usd',
    type: 'one_time' as 'one_time' | 'recurring',
  });

  useEffect(() => {
    fetchStripeConfig();
    fetchStripeProducts();
  }, []);

  const fetchStripeConfig = async () => {
    try {
      const response = await api.get('/payments/config');
      setConfig({
        publishableKey: response.data.publishableKey || '',
        webhookEndpoint: `${window.location.origin}/api/v1/payments/webhook/stripe`,
        isConfigured: !!response.data.publishableKey,
      });
    } catch (error) {
      console.error('Error fetching Stripe config:', error);
      // Mock data for development
      setConfig({
        publishableKey: 'pk_test_51234567890abcdef',
        webhookEndpoint: `${window.location.origin}/api/v1/payments/webhook/stripe`,
        isConfigured: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeProducts = async () => {
    try {
      // TODO: Implementar endpoint para obtener productos de Stripe
      const response = await api.get('/payments/admin/products');
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching Stripe products:', error);
      // Mock data for development
      setProducts([
        {
          id: 'prod_curso_n8n',
          name: 'Automatización Empresarial con n8n',
          description: 'Curso completo de automatización con n8n',
          created: Date.now() / 1000,
          active: true,
          prices: [
            {
              id: 'price_1234567890',
              product: 'prod_curso_n8n',
              unit_amount: 29999,
              currency: 'usd',
              type: 'one_time',
              active: true,
              nickname: 'Precio regular',
            },
          ],
        },
        {
          id: 'prod_subscription_premium',
          name: 'Suscripción Premium',
          description: 'Acceso ilimitado a todos los cursos',
          created: Date.now() / 1000,
          active: true,
          prices: [
            {
              id: 'price_0987654321',
              product: 'prod_subscription_premium',
              unit_amount: 4999,
              currency: 'usd',
              type: 'recurring',
              active: true,
              nickname: 'Mensual',
            },
          ],
        },
      ]);
    }
  };

  const testStripeConnection = async () => {
    setSaving(true);
    try {
      const response = await api.post('/payments/admin/test-connection');
      if (response.data.success) {
        alert('✅ Conexión con Stripe establecida correctamente');
      } else {
        alert('❌ Error al conectar con Stripe: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error testing Stripe connection:', error);
      alert('❌ Error al probar la conexión con Stripe');
    } finally {
      setSaving(false);
    }
  };

  const createStripeProduct = async () => {
    if (!newProduct.name || !newProduct.price) {
      alert('Por favor completa todos los campos requeridos');
      return;
    }

    setSaving(true);
    try {
      const response = await api.post('/payments/admin/products', {
        name: newProduct.name,
        description: newProduct.description,
        price: parseFloat(newProduct.price) * 100, // Convert to cents
        currency: newProduct.currency,
        type: newProduct.type,
      });

      if (response.data.success) {
        await fetchStripeProducts();
        setShowCreateProduct(false);
        setNewProduct({
          name: '',
          description: '',
          price: '',
          currency: 'usd',
          type: 'one_time',
        });
        alert('✅ Producto creado exitosamente en Stripe');
      }
    } catch (error) {
      console.error('Error creating Stripe product:', error);
      alert('❌ Error al crear el producto en Stripe');
    } finally {
      setSaving(false);
    }
  };

  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('es-ES');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configuración de Stripe</h1>
          <p className="text-gray-600">Gestiona la integración con Stripe para pagos</p>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            testMode 
              ? 'bg-yellow-100 text-yellow-800' 
              : 'bg-green-100 text-green-800'
          }`}>
            {testMode ? 'Modo Test' : 'Modo Producción'}
          </span>
        </div>
      </div>

      {/* Configuration Status */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`w-4 h-4 rounded-full ${config.isConfigured ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Estado de la Configuración
              </h3>
              <p className="text-gray-600">
                {config.isConfigured 
                  ? 'Stripe está configurado y listo para procesar pagos'
                  : 'Stripe no está configurado. Configura las claves API para comenzar.'
                }
              </p>
            </div>
          </div>
          <Button onClick={testStripeConnection} disabled={saving}>
            {saving ? 'Probando...' : 'Probar Conexión'}
          </Button>
        </div>
      </Card>

      {/* API Keys Configuration */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Claves de API</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Clave Pública (Publishable Key)
            </label>
            <Input
              type="text"
              value={config.publishableKey}
              placeholder="pk_test_..."
              readOnly
              className="bg-gray-50"
            />
            <p className="text-sm text-gray-500 mt-1">
              Esta clave se obtiene desde el panel de Stripe y se configura en las variables de entorno del servidor.
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Endpoint de Webhook
            </label>
            <Input
              type="text"
              value={config.webhookEndpoint}
              readOnly
              className="bg-gray-50"
            />
            <p className="text-sm text-gray-500 mt-1">
              Configura este endpoint en tu panel de Stripe para recibir eventos de webhook.
            </p>
          </div>
        </div>
      </Card>

      {/* Stripe Products */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Productos de Stripe</h3>
          <Button onClick={() => setShowCreateProduct(true)}>
            Crear Producto
          </Button>
        </div>

        {showCreateProduct && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-md font-medium text-gray-900 mb-3">Crear Nuevo Producto</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Producto *
                </label>
                <Input
                  type="text"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                  placeholder="Ej: Curso de React Avanzado"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Precio (USD) *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                  placeholder="99.99"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Descripción del producto..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Precio
                </label>
                <select
                  value={newProduct.type}
                  onChange={(e) => setNewProduct({ ...newProduct, type: e.target.value as 'one_time' | 'recurring' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="one_time">Pago Único</option>
                  <option value="recurring">Suscripción</option>
                </select>
              </div>
            </div>
            <div className="flex space-x-3 mt-4">
              <Button onClick={createStripeProduct} disabled={saving}>
                {saving ? 'Creando...' : 'Crear Producto'}
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => setShowCreateProduct(false)}
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Producto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Precio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Creado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{product.name}</div>
                      <div className="text-sm text-gray-500">{product.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.prices.map((price) => (
                      <div key={price.id} className="text-sm text-gray-900">
                        {formatPrice(price.unit_amount, price.currency)}
                        {price.nickname && (
                          <span className="text-gray-500 ml-1">({price.nickname})</span>
                        )}
                      </div>
                    ))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.prices.map((price) => (
                      <span
                        key={price.id}
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          price.type === 'recurring'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {price.type === 'recurring' ? 'Suscripción' : 'Pago Único'}
                      </span>
                    ))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      product.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {product.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(product.created)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => window.open(`https://dashboard.stripe.com/products/${product.id}`, '_blank')}
                    >
                      Ver en Stripe
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Setup Instructions */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Instrucciones de Configuración</h3>
        <div className="space-y-4 text-sm text-gray-600">
          <div>
            <h4 className="font-medium text-gray-900">1. Obtener las claves de API</h4>
            <p>Inicia sesión en tu panel de Stripe y ve a Developers → API Keys para obtener tus claves.</p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900">2. Configurar variables de entorno</h4>
            <p>Agrega las siguientes variables a tu archivo de configuración del servidor:</p>
            <div className="bg-gray-100 p-3 rounded mt-2 font-mono text-xs">
              STRIPE_SECRET_KEY=sk_test_...<br/>
              STRIPE_PUBLISHABLE_KEY=pk_test_...<br/>
              STRIPE_WEBHOOK_SECRET=whsec_...
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900">3. Configurar Webhook</h4>
            <p>En tu panel de Stripe, ve a Developers → Webhooks y agrega el endpoint mostrado arriba.</p>
            <p>Eventos recomendados: payment_intent.succeeded, payment_intent.payment_failed, invoice.payment_succeeded</p>
          </div>
        </div>
      </Card>
    </div>
  );
};